
import { useEffect, useRef } from "react"
import nipplejs from "nipplejs"
import type Collection from "nipplejs/Collection"

interface VirtualJoystickProps {
    name: string
    setParentXY: (x: number, y: number) => void
    size?: number
    label?: string
}

export function VirtualJoystick({ name, setParentXY, size, label }: VirtualJoystickProps) {
    const divRef = useRef<HTMLDivElement | null>(null)
    const managerRef = useRef<Collection | null>(null)

    const applyFrontLabel = () => {
        if (!divRef.current) return
        const frontElement = divRef.current.querySelector<HTMLElement>(".front")
        if (!frontElement) return

        if (label) {
            frontElement.setAttribute("data-label", label)
        } else {
            frontElement.removeAttribute("data-label")
        }
    }

    // Initialise the NippleJS joystick manager on component mount
    useEffect(() => {
        if (!divRef.current) return

        // Ensure we don't re-render the joystick twice (Bug with fast double taps)
        if (managerRef.current) managerRef.current.destroy()

        const manager = nipplejs.create({
            zone: divRef.current,
            mode: "static",
            position: { left: "50%", top: "50%" },
            multitouch: false,
            fadeTime: 0, // To avoid double rendering issues & also match buttons styling
            color: {
              front: "rgb(255, 255, 255)",
              back: "rgba(255, 255, 255, 0.25)"
            },
            size: size ?? 100
        })
        managerRef.current = manager as unknown as Collection
        applyFrontLabel()

        manager.on("start", () => {
            console.log(`[Joystick: ${name}] start.`)
            setParentXY(0, 0)
        })
        manager.on("move", (evt) => {
            const x = evt.data.vector.x
            const y = -evt.data.vector.y
            console.log(`[Joystick: ${name}] move:`, { x: x.toFixed(3), y: y.toFixed(3), force: evt.data.force.toFixed(3) })

            setParentXY(x, y)
        })
        manager.on("end", () => {
            console.log(`[Joystick: ${name}] end.`)
            setParentXY(0, 0)
        })
    
        return () => {
            // Cleanup the NippleJS manager on unmount
            manager.destroy()
        }
    }, [])

    useEffect(() => {
        applyFrontLabel()
    }, [label])

    return (
        <>
            {/* NOTE: Joystick zone position must be handled by parent div. */}
            <div className="joystick" ref={divRef}/>
        </>
    )
}